import React, { useState, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, Select } from 'antd'
import { useQuery } from '@apollo/client'
import FormalityStepButton from '../FormalityStepButton'
import { FormInstance } from 'antd/es/form'
import { GREFFES_QUERY } from 'graphql/Greffes'
import { GreffeType } from 'types'
import { CurrentLegalFormalityContext } from 'contexts/LegalFormalityContext'

interface IProps {
  form: FormInstance
  onFinish: any
  loading?: boolean
}

const FormCompletionConfirm = (props: IProps) => {
  const { t } = useTranslation('legalFormalities')
  const { form, onFinish, loading = false } = props
  const { legalFormality } = useContext(CurrentLegalFormalityContext)
  const [formValid, setFormValid] = useState<boolean>(true)
  const { data: greffesData } = useQuery(GREFFES_QUERY)

  // pre-fill with existing values
  React.useEffect(() => {
    form.setFieldsValue({
      depositProvider: legalFormality.depositProvider ?? null,
      noticeProvider: legalFormality.legalNotice?.provider ?? null,
      metadatum: {
        finalLegalAuthorityId: legalFormality.finalLegalAuthorityId
          ? String(legalFormality.finalLegalAuthorityId)
          : null,
      },
    })
  }, [legalFormality])

  const validateFormFields = async () => {
    form
      .validateFields()
      .then(() => setFormValid(true))
      .catch(() => setFormValid(false))
  }

  const formItems = () => {
    return (
      <>
        {t('show.form.completion.title')}
        <Form.Item
          name="depositProvider"
          label={t('show.form.completion.deposit.label')}
          initialValue={legalFormality.depositProvider ?? null}
          rules={[]}
        >
          <Select
            onChange={() => validateFormFields()}
            dropdownStyle={{ zIndex: 10000 }}
          >
            <Select.Option value={null}>
              {t('show.form.completion.options.manuel')}
            </Select.Option>
            <Select.Option value="guichet_unique">
              {t('show.form.completion.options.guichet_unique')}
            </Select.Option>
          </Select>
        </Form.Item>
        <Form.Item
          name="noticeProvider"
          label={t('show.form.completion.notice.label')}
          initialValue={legalFormality.legalNotice?.provider ?? null}
          rules={[]}
        >
          <Select
            onChange={() => validateFormFields()}
            dropdownStyle={{ zIndex: 10000 }}
          >
            <Select.Option value={null}>
              {t('show.form.completion.options.manuel')}
            </Select.Option>
            <Select.Option value="les_echos">
              {t('show.form.completion.options.les_echos')}
            </Select.Option>
          </Select>
        </Form.Item>
        <Form.Item
          name={['metadatum', 'finalLegalAuthorityId']}
          label={t('show.form.completion.greffe.label')}
          initialValue={
            legalFormality.finalLegalAuthorityId
              ? String(legalFormality.finalLegalAuthorityId)
              : null
          }
          rules={[]}
        >
          <Select
            showSearch
            allowClear
            onChange={() => validateFormFields()}
            style={{ minWidth: 300 }}
            dropdownStyle={{ zIndex: 10000 }}
            filterOption={(input, option) =>
              (option?.children as unknown as string)
                ?.toLowerCase()
                .includes(input.toLowerCase())
            }
          >
            {[...(greffesData?.greffes ?? [])]
              .sort((a: GreffeType, b: GreffeType) =>
                a.name.localeCompare(b.name)
              )
              .map((greffe: GreffeType) => (
                <Select.Option
                  key={`greffe-${greffe.id}`}
                  value={String(greffe.id)}
                >
                  {greffe.name}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>
      </>
    )
  }

  return (
    <>
      <FormalityStepButton
        title={t('show.form.submit.confirm.next.title')}
        description={formItems()}
        buttonDisabled={loading}
        buttonLabel={t('show.form.submit.confirm.next.label')}
        onConfirm={() => onFinish(form.getFieldsValue())}
        okConfirmDisabled={!formValid}
      />
    </>
  )
}

export default FormCompletionConfirm
